import React, { useState } from 'react';
import { Button, Card, Alert, Space, Spin, Collapse, Tag, message } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, WarningOutlined, ReloadOutlined } from '@ant-design/icons';
import { StateMachineService } from '../../services/api/stateMachineService';
import { StateMachineValidationResult } from '../../types/stateMachine';

const { Panel } = Collapse;

interface StateMachineValidatorProps {
  artifactId: string;
  onValidationComplete?: (result: StateMachineValidationResult) => void;
  autoValidate?: boolean;
  compact?: boolean;
}

export const StateMachineValidator: React.FC<StateMachineValidatorProps> = ({
  artifactId,
  onValidationComplete,
  autoValidate = false,
  compact = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<StateMachineValidationResult | null>(null);
  const [validationOptions, setValidationOptions] = useState({
    validate_completeness: true,
    validate_reachability: true,
    validate_consistency: true,
    identify_bottlenecks: false,
  });

  React.useEffect(() => {
    if (autoValidate && artifactId) {
      void runValidation();
    }
  }, [autoValidate, artifactId]);

  const runValidation = async () => {
    if (!artifactId) return;

    try {
      setValidating(true);
      const result = await StateMachineService.validateStateMachine(artifactId, validationOptions);
      setValidationResult(result);
      onValidationComplete?.(result);

      if (result.is_valid) {
        message.success('State machine validation passed');
      } else {
        message.warning(`State machine validation found ${result.errors.length} error(s)`);
      }
    } catch (error: any) {
      console.error('Error validating state machine:', error);
      message.error('Failed to validate state machine');
    } finally {
      setValidating(false);
    }
  };

  const getStatusIcon = () => {
    if (!validationResult) return null;

    if (validationResult.is_valid) {
      return <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 24 }} />;
    } else {
      return <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 24 }} />;
    }
  };

  const getStatusTag = () => {
    if (!validationResult) return null;

    if (validationResult.is_valid) {
      return <Tag color="success">Valid</Tag>;
    } else {
      return <Tag color="error">Invalid</Tag>;
    }
  };

  return (
    <Card
      title={
        <Space>
          <span>State Machine Validation</span>
          {getStatusTag()}
        </Space>
      }
      extra={
        <Button
          icon={<ReloadOutlined />}
          onClick={runValidation}
          loading={validating}
          size={compact ? 'small' : 'middle'}
        >
          Validate
        </Button>
      }
      size={compact ? 'small' : 'default'}
    >
      {validating ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>Validating state machine...</div>
        </div>
      ) : validationResult ? (
        <div>
          <div style={{ marginBottom: 16, textAlign: 'center' }}>
            {getStatusIcon()}
            <div style={{ marginTop: 8 }}>
              <strong>
                {validationResult.is_valid
                  ? 'State machine is valid'
                  : `State machine has ${validationResult.errors.length} error(s)`}
              </strong>
            </div>
            {validationResult.states_count !== undefined && (
              <div style={{ marginTop: 8, color: '#666' }}>
                States: {validationResult.states_count} | Transitions:{' '}
                {validationResult.transitions_count}
              </div>
            )}
          </div>

          {validationResult.errors.length > 0 && (
            <Alert
              message="Errors"
              description={
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {validationResult.errors.map((error, idx) => (
                    <li key={idx}>{error}</li>
                  ))}
                </ul>
              }
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {validationResult.warnings.length > 0 && (
            <Alert
              message="Warnings"
              description={
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {validationResult.warnings.map((warning, idx) => (
                    <li key={idx}>{warning}</li>
                  ))}
                </ul>
              }
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {(validationResult.orphaned_states?.length ||
            validationResult.unreachable_states?.length ||
            validationResult.deadlock_states?.length ||
            validationResult.cycles?.length) && (
            <Collapse style={{ marginTop: 16 }}>
              {validationResult.orphaned_states && validationResult.orphaned_states.length > 0 && (
                <Panel header="Orphaned States" key="orphaned">
                  <ul>
                    {validationResult.orphaned_states.map((state, idx) => (
                      <li key={idx}>{state}</li>
                    ))}
                  </ul>
                </Panel>
              )}

              {validationResult.unreachable_states &&
                validationResult.unreachable_states.length > 0 && (
                  <Panel header="Unreachable States" key="unreachable">
                    <ul>
                      {validationResult.unreachable_states.map((state, idx) => (
                        <li key={idx}>{state}</li>
                      ))}
                    </ul>
                  </Panel>
                )}

              {validationResult.deadlock_states && validationResult.deadlock_states.length > 0 && (
                <Panel header="Deadlock States" key="deadlock">
                  <ul>
                    {validationResult.deadlock_states.map((state, idx) => (
                      <li key={idx}>{state}</li>
                    ))}
                  </ul>
                </Panel>
              )}

              {validationResult.cycles && validationResult.cycles.length > 0 && (
                <Panel header="Cycles" key="cycles">
                  <ul>
                    {validationResult.cycles.map((cycle, idx) => (
                      <li key={idx}>{cycle}</li>
                    ))}
                  </ul>
                </Panel>
              )}
            </Collapse>
          )}

          {validationResult.is_valid &&
            validationResult.warnings.length === 0 &&
            (!validationResult.orphaned_states || validationResult.orphaned_states.length === 0) &&
            (!validationResult.unreachable_states ||
              validationResult.unreachable_states.length === 0) && (
              <Alert
                message="State machine is valid and complete"
                type="success"
                showIcon
                style={{ marginTop: 16 }}
              />
            )}
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: 24, color: '#999' }}>
          Click "Validate" to check the state machine
        </div>
      )}
    </Card>
  );
};

